import { useEffect, useRef, useState } from 'react'
import { useFaeden, useFadenSetzen } from '../../lib/mywork'

/**
 * **Der Faden** ([Z-24](../../../../../docs/phasen/zusatz-24-my-work.md) Stufe 4, E-453) – *wo stehe
 * ich, was ist der nächste Schritt*. Gemerkt beim Wechsel, gezeigt beim Zurückkommen: der am besten
 * belegte Hebel gegen das, was ein Kontextwechsel im Kopf zurücklässt (Leroy & Glomb 2018,
 * „ready-to-resume plan").
 *
 * Privat (`aufgabe_faden`), nie im Team, nie an die KI.
 */

/** Wie lange her – „gerade", „vor 12 min", „vor 3 h", „gestern 16:40", „Mo 16:40". */
export function wannText(iso: string | Date, jetzt = new Date()): string {
  const d = new Date(iso)
  const min = Math.round((jetzt.getTime() - d.getTime()) / 60_000)
  if (min < 2) return 'gerade'
  if (min < 60) return `vor ${min} min`
  const gleicherTag = d.toDateString() === jetzt.toDateString()
  if (gleicherTag) return `vor ${Math.round(min / 60)} h`
  const uhr = d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })
  const gestern = new Date(jetzt)
  gestern.setDate(gestern.getDate() - 1)
  if (d.toDateString() === gestern.toDateString()) return `gestern ${uhr}`
  return `${d.toLocaleDateString('de-DE', { weekday: 'short' })} ${uhr}`
}

/** Der Faden oben in der Puls-Karte – nur, wenn es einen gibt. */
export function FadenAnzeige({ taskId, aendern }: { taskId: string; aendern: () => void }) {
  const faeden = useFaeden()
  const f = faeden.get(taskId)
  if (!f) return null
  return (
    <div className="mt-3 flex items-start gap-3 rounded-xl border border-[#dcefe5] bg-[#f7fbf9] px-3.5 py-2.5 text-[13.5px]">
      <span aria-hidden="true" className="text-[16px] leading-tight text-good-text">
        ↳
      </span>
      <div className="min-w-0 flex-1">
        <div className="text-[10.5px] font-bold tracking-[0.08em] text-good-text uppercase">
          Dein Faden · {wannText(f.aktualisiertAm)}
        </div>
        {f.stand && <div className="mt-0.5">{f.stand}</div>}
        {f.naechsterSchritt && (
          <div className="mt-0.5">
            <b>Nächster Schritt:</b> {f.naechsterSchritt}
          </div>
        )}
      </div>
      <button
        type="button"
        onClick={aendern}
        className="cursor-pointer text-[12px] font-semibold text-accent-deep"
      >
        ändern
      </button>
    </div>
  )
}

/**
 * **Das Blatt beim Wechsel** – zwei Zeilen, vorbelegt mit dem letzten Faden. „Merken" speichert und
 * führt den Wechsel aus, „ohne Faden weiter" nur den Wechsel; Esc oder ein Klick daneben bricht ab.
 */
export function FadenBlatt({
  taskId,
  titel,
  anlass,
  weiter,
  abbrechen,
  weiterText = 'Merken',
}: {
  taskId: string
  titel: string
  /** Was gerade passiert – „Gleich: Baubesprechung (13:30)" oder „Puls beenden". */
  anlass: string
  /** Wird nach „Merken" und nach „ohne Faden weiter" ausgeführt. */
  weiter: () => void
  abbrechen: () => void
  weiterText?: string
}) {
  const faeden = useFaeden()
  const alt = faeden.get(taskId)
  const [stand, setStand] = useState(alt?.stand ?? '')
  const [schritt, setSchritt] = useState(alt?.naechsterSchritt ?? '')
  const setzen = useFadenSetzen()
  const erstes = useRef<HTMLInputElement>(null)

  useEffect(() => {
    erstes.current?.focus()
    const taste = (e: KeyboardEvent) => {
      if (e.key === 'Escape') abbrechen()
    }
    window.addEventListener('keydown', taste)
    return () => window.removeEventListener('keydown', taste)
  }, [abbrechen])

  const merken = () => {
    if (stand.trim() || schritt.trim()) {
      setzen.mutate({ taskId, stand: stand.trim(), naechsterSchritt: schritt.trim() })
    }
    weiter()
  }

  return (
    <div
      className="unter-leiste fixed inset-x-0 bottom-0 z-[70] flex items-end justify-center bg-[rgb(23_22_20/0.32)] sm:items-center"
      role="dialog"
      aria-modal="true"
      aria-label="Faden merken"
      onClick={abbrechen}
    >
      <form
        className="flex w-full max-w-[480px] flex-col gap-3.5 rounded-t-[22px] border border-line bg-surface p-5 pb-[calc(20px+env(safe-area-inset-bottom))] shadow-lg sm:rounded-card sm:pb-5"
        onClick={(e) => e.stopPropagation()}
        onSubmit={(e) => {
          e.preventDefault()
          merken()
        }}
      >
        <div className="rounded-xl border border-[#f1d39a] bg-[#fff8ea] px-3 py-2 text-[13px] font-semibold text-[#8a5300]">
          {anlass}
        </div>
        <div>
          <h2 className="text-[18px] leading-snug font-bold">Wo stehst du bei „{titel}"?</h2>
          <p className="mt-0.5 text-[13px] text-ink2">
            Eine Zeile reicht. Sie steht oben, wenn du zurückkommst.
          </p>
        </div>
        <label className="flex flex-col gap-1 rounded-xl border-[1.5px] border-[#9ed7bf] bg-[#f7fbf9] px-3 py-2">
          <span className="text-[10.5px] font-bold tracking-[0.08em] text-good-text uppercase">
            Stand
          </span>
          <input
            ref={erstes}
            value={stand}
            maxLength={1000}
            onChange={(e) => setStand(e.target.value)}
            placeholder="z. B. dd.mm. geht, ISO fehlt noch"
            className="border-0 bg-transparent text-[16px] outline-none"
          />
        </label>
        <label className="flex flex-col gap-1 rounded-xl border-[1.5px] border-line2 px-3 py-2">
          <span className="text-[10.5px] font-bold tracking-[0.08em] text-ink2 uppercase">
            Nächster Schritt
          </span>
          <input
            value={schritt}
            maxLength={300}
            onChange={(e) => setSchritt(e.target.value)}
            placeholder="z. B. weiter mit Zeitzonen"
            className="border-0 bg-transparent text-[16px] outline-none"
          />
        </label>
        <div className="flex flex-col gap-1.5">
          <button
            type="submit"
            className="min-h-[50px] cursor-pointer rounded-[14px] bg-accent text-[16px] font-bold text-white"
          >
            {weiterText}
          </button>
          <button
            type="button"
            onClick={weiter}
            className="min-h-11 cursor-pointer rounded-xl text-[14px] text-ink2"
          >
            ohne Faden weiter
          </button>
        </div>
      </form>
    </div>
  )
}
